/**
 * AgeGateModal — soft 18+ confirmation for Past Lives Pack.
 *
 * Two modes (driven by computed age from User.birthDate, see lib/age.ts):
 *   - 'soft-under-18' — age < 18: warn that material may touch mature
 *     historical themes; require explicit Continue tap.
 *   - 'hard-missing'  — birthDate unknown: block with "complete profile first".
 *
 * If age >= 18, the flow skips this modal entirely (decided by the orchestrator,
 * not here).
 */

import { useTranslation } from 'react-i18next';
import styles from './AgeGateModal.module.css';

export type AgeGateMode = 'soft-under-18' | 'hard-missing';

export interface AgeGateModalProps {
  mode: AgeGateMode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function AgeGateModal({ mode, onConfirm, onCancel }: AgeGateModalProps): JSX.Element {
  const { t } = useTranslation('flows');
  const titleKey = mode === 'hard-missing' ? 'pastLives.ageGate.missing.title' : 'pastLives.ageGate.soft.title';
  const bodyKey  = mode === 'hard-missing' ? 'pastLives.ageGate.missing.body'  : 'pastLives.ageGate.soft.body';
  const confirmKey = mode === 'hard-missing' ? 'pastLives.ageGate.missing.confirm' : 'pastLives.ageGate.soft.confirm';

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" data-testid="past-lives-age-gate">
      <div className={styles.modal}>
        <h2 className={styles.title}>{t(titleKey)}</h2>
        <p className={styles.body}>{t(bodyKey)}</p>
        <div className={styles.actions}>
          {mode !== 'hard-missing' ? (
            <button type="button" className={styles.primary} onClick={onConfirm}>
              {t(confirmKey)}
            </button>
          ) : null}
          <button type="button" className={styles.secondary} onClick={onCancel}>
            {t('pastLives.ageGate.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
